import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Button } from "../../../ui/button";
import { Badge } from "../../../ui/badge";
import { Tooltip } from "../../../ui/tooltip";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../../ui/collapsible";
import { 
  FunctionSquare,
  Edit,
  ChevronDown,
  ChevronRight,
  Wrench,
} from "lucide-react";
import { ToolCall } from "../utils/types";



interface ToolCallWithNodeInfo extends ToolCall {
  nodeId: string;
  nodeIdentifier: string;
  nodeEntityType: string;
}

interface ToolCallsTabProps {
  nodes: any[];
  onNodeFocus?: (nodeId: string) => void;
  onEditToolCall?: (originalSignature: string, toolCall: ToolCall) => void;
}

const ToolCallsTab: React.FC<ToolCallsTabProps> = ({ 
  nodes, 
  onNodeFocus, 
  onEditToolCall 
}) => {
  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);
  /**
   * Creates a signature string from a tool call for grouping/comparison
   */
  const getToolCallSignature = (toolCall: ToolCall): string => {
    if (!toolCall) return "";
    
    const paramsString = toolCall.parameters
      ?.map(p => `${p.name}:${p.value}`)
      ?.sort() // Sort to ensure consistent ordering
      ?.join("|") || "";
      
    return `${toolCall.name}|${toolCall.description || ""}|${paramsString}`;
  };

  /**
   * Extract all tool calls from BOT nodes with their node information
   */
  const getAllToolCalls = (): ToolCallWithNodeInfo[] => {
    const toolCalls: ToolCallWithNodeInfo[] = [];
    
    nodes.forEach((node) => {
      if (
        node.data.entityType === "BOT" && 
        node.data.toolCalls && 
        Array.isArray(node.data.toolCalls) &&
        node.data.toolCalls.length > 0 &&
        !node.data.isPlaceholder
      ) {
        // We only support one tool call per node, so take the first one
        const toolCall = node.data.toolCalls[0];
        if (toolCall.name && toolCall.name.trim()) {
          toolCalls.push({
            ...toolCall,
            nodeId: node.id,
            nodeIdentifier: node.data.identifier || "Unclassified",
            nodeEntityType: node.data.entityType,
          });
        }
      }
    });
    
    return toolCalls;
  };

  /**
   * Group tool calls by their signature
   */
  const getGroupedToolCalls = () => {
    const toolCalls = getAllToolCalls();
    
    const grouped = toolCalls.reduce((acc, toolCall) => {
      const signature = getToolCallSignature(toolCall);
      if (!acc[signature]) {
        acc[signature] = [];
      }
      acc[signature].push(toolCall);
      return acc;
    }, {} as Record<string, ToolCallWithNodeInfo[]>);
    
    return grouped;
  };

  const groupedToolCalls = getGroupedToolCalls();
  const uniqueSignatures = Object.keys(groupedToolCalls);

  /**
   * Handle editing a tool call from the sidebar (global update)
   */
  const handleEditToolCallGroup = (signature: string, toolCall: ToolCall) => {
    // console.log("📋 ToolCallsTab: Initiating global edit for signature:", signature);
    onEditToolCall?.(signature, toolCall);
  };

  if (uniqueSignatures.length === 0) {
    return (
      <div className="p-6 h-[calc(100vh-160px)] overflow-y-auto overflow-x-hidden">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="space-y-3">
            <Wrench className="w-12 h-12 text-muted-foreground mx-auto" />
            <h3 className="text-lg font-gc-med text-foreground">
              No tool calls found
            </h3>
            <p className="text-muted-foreground text-sm max-w-md">
              Add tool calls to BOT nodes to see them here
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 h-[calc(100vh-160px)] overflow-y-auto overflow-x-hidden">
      <div className="space-y-6">
        {uniqueSignatures.map((signature) => {
          const toolInstances = groupedToolCalls[signature];
          const firstInstance = toolInstances[0]; // Use first instance as representative
          
          return (
            <Card key={signature} className="border-border">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FunctionSquare className="w-4 h-4 text-maitai-green" />
                    <CardTitle className="text-sm font-gc-med text-foreground">
                      {firstInstance.name}
                    </CardTitle>
                    <Badge variant="secondary" className="text-xs">
                      {toolInstances.length} node{toolInstances.length > 1 ? "s" : ""}
                    </Badge>
                  </div>
                  <Tooltip content="Edit this tool call (will update all nodes using it)" side="left" immediate={true}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditToolCallGroup(signature, firstInstance)}
                      className="h-8 w-8 p-0 text-maitai-green hover:text-maitai-green/80"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  </Tooltip>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Tool Call Details */}
                <div>
                  <h4 className="text-sm font-gc-med text-foreground mb-2">
                    Description:
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {firstInstance.description || "No description"}
                  </p>
                </div>

                {/* Parameters */}
                {firstInstance.parameters && firstInstance.parameters.length > 0 && (
                  <div>
                    <h4 className="text-sm font-gc-med text-foreground mb-2">
                      Parameters ({firstInstance.parameters.length}):
                    </h4>
                    <div className="bg-accent/20 p-2 rounded-md border border-border">
                      <code className="text-xs font-mono text-muted-foreground">
                        {firstInstance.parameters.map(p => `${p.name}=${p.value}`).join(", ")}
                      </code>
                    </div>
                  </div>
                )}

                {/* Used In Section */}
                <Collapsible 
                  open={expandedNodes.includes(`nodes-${signature}`)}
                  onOpenChange={(open: boolean) => {
                    const key = `nodes-${signature}`;
                    setExpandedNodes(prev => 
                      open 
                        ? [...prev, key]
                        : prev.filter(k => k !== key)
                    );
                  }}
                >
                  <CollapsibleTrigger className="flex items-center gap-2 w-full text-left p-0 hover:no-underline">
                    <div className="flex items-center gap-2">
                      {expandedNodes.includes(`nodes-${signature}`) ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                      <span className="text-sm font-gc-med text-foreground">
                        Used in {toolInstances.length} node{toolInstances.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-2">
                    <div className="max-h-[120px] overflow-auto space-y-1">
                      {toolInstances.map((instance, index) => (
                        <Tooltip key={instance.nodeId} content="Click to focus on this node" side="left" immediate={true}>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onNodeFocus?.(instance.nodeId)}
                            className="w-full justify-start h-auto p-2 text-left border border-border bg-accent/20 hover:bg-accent/40"
                          >
                          <span className="font-bold text-maitai-green text-xs">
                            #{index + 1}
                          </span>
                            <span className="text-foreground text-xs ml-2">
                              {instance.nodeIdentifier}
                            </span>
                            <span className="text-muted-foreground text-xs ml-2">
                              ({instance.nodeEntityType})
                            </span>
                          </Button>
                        </Tooltip>
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default ToolCallsTab; 